import { UploadedFile } from "@/shared/types/file-upload";
import { EvidenceDocument } from "../../requests";
import FileUpload from "@/shared/components/custom/file-upload";
import { doesFileHasError } from "@/shared/components/custom/file-upload/helper";

const DefaultAllowedFileTypes = [
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
  "application/json",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "image/png",
  "image/jpeg",
  "image/jpg",
  "text/csv",
];

interface DocumentItemProps {
  itemNumber: number;
  document: EvidenceDocument;
  files: UploadedFile[];
  onFilesChange: (documentId: string, files: UploadedFile[]) => void;
  onUploadError: (documentId: string, hasError: boolean) => void;
  isSaving?: boolean;
  customDeleteHandler?: (signedUrl: string, fileId: string) => Promise<void>;
  onUploadStart?: (documentId: string) => void;
  onUploadEnd?: (documentId: string) => void;
  isReadOnly?: boolean;
}

const DocumentItem: React.FC<DocumentItemProps> = ({
  itemNumber,
  document,
  files,
  onFilesChange,
  onUploadError,
  isSaving = false,
  customDeleteHandler,
  onUploadStart,
  onUploadEnd,
  isReadOnly = false,
}) => {
  const handleFileChange = (newFiles: UploadedFile[]) => {
    const hasError = newFiles.some(file => doesFileHasError(file));
    onUploadError(document.id, hasError);
    onFilesChange(document.id, newFiles);
  };

  // Handle upload status change to track when uploads are in progress
  const handleUploadStatusChange = (isUploading: boolean) => {
    if (isUploading) {
      onUploadStart?.(document.id);
    } else {
      onUploadEnd?.(document.id);
    }
  };

  // Calculate the width needed for item number to ensure proper alignment (similar to QuestionField)
  const itemNumberText = `${itemNumber}.`;
  const itemNumberWidth = Math.max(20, itemNumberText.length * 6 + 4);

  return (
    <div className="flex flex-col items-start p-0 gap-1.5 w-full">
      {/* Document Header - similar to QuestionField */}
      <div className="flex flex-row items-start p-0 gap-2 w-full min-h-5">
        {/* Item number */}
        <div
          className="flex-shrink-0 flex items-start justify-start"
          style={{ minWidth: `${itemNumberWidth}px` }}
        >
          <span
            className="text-sm leading-5 font-normal text-[#3F3F46]"
            style={{ fontFamily: 'Inter' }}
          >
            {itemNumber}.
          </span>
        </div>

        {/* Document name */}
        <span
          className="flex-1 text-sm leading-5 font-normal text-[#3F3F46]"
          style={{ fontFamily: 'Inter' }}
        >
          {document.document_name}
          {document.required && <span className="text-red-500 ml-1">*</span>}
        </span>
      </div>

      {/* File Upload Section - aligned with question fields */}
      <div className="flex flex-row items-start p-0 gap-2 w-full">
        {/* Empty space for alignment */}
        <div className="flex-shrink-0" style={{ minWidth: `${itemNumberWidth}px` }} />

        {/* File upload area */}
        <div className="flex-1">
          <FileUpload
            maxFiles={document.max_number_of_files}
            orientation="horizontal"
            isMultiple={document.max_number_of_files > 1}
            allowedFileTypes={DefaultAllowedFileTypes}
            setFiles={handleFileChange}
            maxSizeMB={20}
            initialFiles={files}
            customDeleteHandler={customDeleteHandler}
            onUploadStatusChange={handleUploadStatusChange}
            isDisabled={isReadOnly}
          />
        </div>
      </div>
    </div>
  );
};

export default DocumentItem;
